import React from "react";
import { useSearchParams } from "react-router-dom";
import { useCurrentSubscription } from "@hooks/query/useCurrentSubscription";
import { useProducts } from "@hooks/query/useProducts";
import { useRevenueCatOffering } from "@hooks/query/useRevenueCatOffering";
import { useLoading } from "@hooks/useLoading";
import { showSuccessMsg } from "@utils/notification";
import { Product } from "@remotes/fetchProducts";
import { redirectToRevenueCatPurchase } from "@remotes/createRevenueCatPurchaseLink";
import { redirectToRevenueCatPortal } from "@remotes/createRevenueCatPortal";
import { createRevenueCatPaywallUrl } from "@remotes/fetchRevenueCatOffering";
import { getTokens } from "@utils/authStorage";

export const useSubscriptionLogic = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const { data: subscription, isLoading: subscriptionLoading, refetch } = useCurrentSubscription();
  const { data: products, isLoading: productsLoading } = useProducts();
  const { data: revenueCatOffering, isLoading: revenueCatLoading } = useRevenueCatOffering();
  const [loading, startLoading, stopLoading] = useLoading();
  const [checkoutLoading, setCheckoutLoading] = React.useState<string | null>(null);

  const handleRefreshCredits = async () => {
    try {
      startLoading();
      await refetch();
      showSuccessMsg("Subscription data refreshed successfully!");
    } catch (error) {
      console.error("Error refreshing subscription:", error);
      alert("Failed to refresh subscription data");
    } finally {
      stopLoading();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case "FREE":
        return "#ffffff";
      case "LITE":
        return "#4caf50";
      case "BASIC":
        return "#0f7dff";
      case "PRO":
        return "#ffc107";
      default:
        return "#ffffff";
    }
  };

  const getRenewableTypeLabel = (type: string) => {
    switch (type) {
      case "MONTHLY":
        return "Monthly";
      case "YEARLY":
        return "Yearly";
      case "NONRENEWABLE":
        return "One-time";
      default:
        return type;
    }
  };

  const isTrialOrNonRenewable = () => {
    return subscription?.period_type === "TRIAL" || subscription?.product.renewable_type === "NONRENEWABLE";
  };

  const isTrial = () => {
    return subscription?.period_type === "TRIAL";
  };

  const isAppleSubscription = React.useCallback(() => {
    return subscription?.payment_source === "APPLE";
  }, [subscription]);

  const isRCBillingSubscription = React.useCallback(() => {
    return (subscription?.payment_source as string) === "RC_BILLING";
  }, [subscription]);

  const getPlatformDisplayName = React.useCallback(() => {
    if (!subscription) return "Unknown";
    
    if (isAppleSubscription()) {
      return "iOS App Store";
    }
    
    return "RevenueCat Web";
  }, [subscription, isAppleSubscription]);

  const filterProducts = (products: Product[]) => {
    if (!subscription) return products;

    // Define the expected RevenueCat product IDs for each plan
    const productIdMap = [
      { tier: "BASIC", renewable_type: "MONTHLY", price: "$9.99/mo", revenue_cat_product_id: "basic_monthly_sub" },
      { tier: "BASIC", renewable_type: "YEARLY", price: "$71.99/yr", revenue_cat_product_id: "basic_yearly_sub" },
      { tier: "PRO", renewable_type: "MONTHLY", price: "$39.99/mo", revenue_cat_product_id: "pro_monthly_sub" },
      { tier: "PRO", renewable_type: "YEARLY", price: "$229/yr", revenue_cat_product_id: "pro_yearly_sub" },
    ];

    // Calculate discounts for yearly plans
    const calculateDiscount = (monthlyPrice: number, yearlyPrice: number) => {
      const monthlyTotal = monthlyPrice * 12;
      const savings = monthlyTotal - yearlyPrice;
      const discountPercentage = Math.round((savings / monthlyTotal) * 100);
      return { savings, discountPercentage };
    };

    const basicDiscount = calculateDiscount(9.99, 71.99);
    const proDiscount = calculateDiscount(39.99, 229);

    // Map each slot to the correct product from the backend, only if the ID matches
    const orderedProducts = productIdMap
      .map(slot => {
        const matchingProduct = products.find(product =>
          product.tier === slot.tier &&
          product.renewable_type === slot.renewable_type &&
          product.revenue_cat_product_id === slot.revenue_cat_product_id
        );
        if (matchingProduct) {
          let discount = null;
          if (slot.renewable_type === "YEARLY") {
            if (slot.tier === "BASIC") {
              discount = basicDiscount;
            } else if (slot.tier === "PRO") {
              discount = proDiscount;
            }
          }
          return { 
            ...matchingProduct, 
            price: slot.price,
            discount: discount
          };
        }
        return null;
      })
      .filter(Boolean) as Product[];

    // Exclude current subscription product if user has one
    const finalProducts = orderedProducts.filter(product => product.id !== subscription.product.id);
    return finalProducts;
  };

  const handleUpgradeClick = async (product: Product) => {
    console.log("Attempting to upgrade to:", product);
    console.log("Product revenue_cat_product_id:", product.revenue_cat_product_id);
    
    if (!product.revenue_cat_product_id) {
      alert(`This product (${product.display_name}) is not available for web purchase yet. Please contact support.`);
      return;
    }
    
    if (isAppleSubscription()) {
      alert("Please manage your subscription through the iOS App Store. You can find this in Settings > Apple ID > Subscriptions.");
    } else {
      try {
        setCheckoutLoading(product.id);
        
        const currentUrl = window.location.origin + window.location.pathname;
        const successUrl = `${currentUrl}?status=success`;
        const cancelUrl = `${currentUrl}?status=cancelled`;
        
        redirectToRevenueCatPurchase({
          product,
          successUrl,
          cancelUrl,
          metadata: {
            upgrade_from: subscription?.product.tier || "UNKNOWN",
            subscription_id: subscription?.id || "",
            current_platform: isAppleSubscription() ? "APPLE" : "REVENUECAT_WEB"
          }
        });
      } catch (error) {
        console.error("Error creating RevenueCat purchase link:", error);
        alert("Failed to create purchase link. Please try again.");
        setCheckoutLoading(null);
      }
    }
  };

  const handleManageSubscription = async () => {
    if (isAppleSubscription()) {
      alert("To manage your subscription, please use an iOS device and visit the App Store.");
    } else {
      try {
        await redirectToRevenueCatPortal({
          returnUrl: window.location.href
        });
      } catch (error) {
        alert("RevenueCat customer portal is coming soon! For now, please contact our support team to manage your subscription.");
      }
    }
  };

  const handleCancelSubscription = async () => {
    if (isAppleSubscription()) {
      alert("To cancel your subscription, please use an iOS device and visit Settings > Apple ID > Subscriptions.");
    } else {
      try {
        await redirectToRevenueCatPortal({
          returnUrl: window.location.href
        });
      } catch (error) {
        alert("Self-service cancellation is coming soon! For now, please contact our support team to cancel your subscription.");
      }
    }
  };

  const availableProducts = products ? filterProducts(products) : [];

  const handleChooseSubscription = () => {
    // Create paywall URL with user ID for personalized experience
    console.log("🔍 [Choose Subscription] Subscription object:", subscription);
    console.log("🔍 [Choose Subscription] subscription.user (numeric ID):", subscription?.user);
    
    // Get the correct UUID from userData instead of using subscription.user
    const { userData } = getTokens();
    console.log("🔍 [Choose Subscription] Retrieved userData from storage:", userData);
    console.log("🔍 [Choose Subscription] User UUID from userData:", userData?.uuid);
    
    if (userData?.uuid) {
      const paywallUrl = createRevenueCatPaywallUrl(userData.uuid);
      console.log("🔗 [Choose Subscription] Generated paywall URL with UUID:", paywallUrl);
      console.log("🎯 [Choose Subscription] URL contains UUID:", paywallUrl.includes(userData.uuid));
      
      // Log the URL without redirecting for debugging
      console.log("🔗 [DEBUG] Full paywall URL (copy this to test):");
      console.log(paywallUrl);
      
      window.open(paywallUrl, '_blank');
    } else {
      // Fallback to generic paywall URL
      console.log("⚠️ [Choose Subscription] No UUID found, using generic paywall URL");
      const genericUrl = 'https://pay.rev.cat/ooscfkocujqxgqrh/';
      console.log("🔗 [DEBUG] Generic paywall URL:");
      console.log(genericUrl);
      window.open(genericUrl, '_blank');
    }
  };

  const handleManageSubscriptionUrl = () => {
    if (subscription?.management_url) {
      window.open(subscription.management_url, '_blank');
    }
  };

  return {
    status,
    subscription,
    subscriptionLoading,
    productsLoading,
    revenueCatLoading,
    loading,
    checkoutLoading,
    availableProducts,
    revenueCatOffering,
    isTrialOrNonRenewable,
    isTrial,
    isAppleSubscription,
    isRCBillingSubscription,
    getPlatformDisplayName,
    handleRefreshCredits,
    formatDate,
    getTierColor,
    getRenewableTypeLabel,
    handleUpgradeClick,
    handleManageSubscription,
    handleCancelSubscription,
    handleChooseSubscription,
    handleManageSubscriptionUrl,
  };
}; 